// Owner-side audit log. Shows who did what — stage changes, contact edits, etc.
// Pulls from /api/settings/audit-log. Filters by action + actor.
import React, { useEffect, useState, useMemo } from 'react';
import { G, styles, api } from '../theme.js';
import { Spinner, Input, Select, Badge } from '../ui.jsx';

const ACTION_LABEL = {
  stage_changed:    { label:'Stage moved',     icon:'🎯', color:G.purple },
  contact_updated:  { label:'Contact updated', icon:'✏️', color:G.cyan },
  contact_created:  { label:'Contact created', icon:'➕', color:G.green },
  contact_deleted:  { label:'Contact removed', icon:'🗑️', color:G.red },
  invoice_created:  { label:'Invoice created', icon:'💰', color:G.yellow },
  invoice_paid:     { label:'Invoice paid',    icon:'✅', color:G.green },
  payment_collected:{ label:'Payment collected',icon:'💳', color:G.green },
  job_created:      { label:'Job created',     icon:'🔧', color:G.blue },
  user_login:       { label:'Login',           icon:'🔑', color:G.textMuted },
};

function fmtDiff(beforeStr, afterStr) {
  let b = {}, a = {};
  try { b = JSON.parse(beforeStr||'{}'); } catch {}
  try { a = JSON.parse(afterStr ||'{}'); } catch {}
  const keys = new Set([...Object.keys(b||{}), ...Object.keys(a||{})]);
  const out = [];
  for (const k of keys) {
    const bv = JSON.stringify(b[k]);
    const av = JSON.stringify(a[k]);
    if (bv !== av) out.push({ key: k, before: b[k], after: a[k] });
  }
  return out;
}

export function ActivityLog({ user, toast }) {
  const [rows, setRows] = useState(null);
  const [actionFilter, setActionFilter] = useState('');
  const [search, setSearch] = useState('');

  const load = () => {
    api(`/api/settings/audit-log${actionFilter ? `?action=${actionFilter}` : ''}`)
      .then(setRows)
      .catch(e => { setRows([]); toast(e.message,'error'); });
  };
  useEffect(() => { load(); }, [actionFilter]);

  const filtered = useMemo(() => {
    if (!rows) return null;
    if (!search) return rows;
    const s = search.toLowerCase();
    return rows.filter(r =>
      (r.actor_email||'').toLowerCase().includes(s) ||
      (r.action||'').toLowerCase().includes(s) ||
      (r.entity||'').toLowerCase().includes(s)
    );
  }, [rows, search]);

  return <div style={{ padding:24 }}>
    <div style={{ marginBottom:20 }}>
      <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>Activity Log</h1>
      <div style={{ color:G.textMuted, marginTop:4, fontSize:13 }}>Every meaningful change to your account — who, what, when. Use this to investigate "wait, who did that?"</div>
    </div>

    <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap' }}>
      <Input placeholder="Search by user, action, entity…" value={search} onChange={e=>setSearch(e.target.value)} style={{ maxWidth:340 }}/>
      <Select value={actionFilter} onChange={e=>setActionFilter(e.target.value)} style={{ maxWidth:220 }}>
        <option value="">All actions</option>
        {Object.entries(ACTION_LABEL).map(([k,v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
      </Select>
    </div>

    {!filtered ? <div style={{ display:'flex', justifyContent:'center', padding:80 }}><Spinner sz={32}/></div>
      : filtered.length === 0 ? <div style={{ ...styles.card, padding:48, textAlign:'center', color:G.textMuted }}>No activity yet — events will appear here as they happen.</div>
      : <div style={{ ...styles.card, padding:0, overflow:'hidden' }}>
        {filtered.map(r => {
          const meta = ACTION_LABEL[r.action] || { label: r.action || 'Event', icon:'•', color:G.textMuted };
          const diff = fmtDiff(r.before, r.after);
          return <div key={r.id} style={{ padding:'14px 18px', borderBottom:`1px solid ${G.border}`, display:'flex', gap:14 }}>
            <div style={{ fontSize:18, lineHeight:1, width:24, textAlign:'center', marginTop:2 }}>{meta.icon}</div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                <span style={{ fontWeight:700, color:meta.color }}>{meta.label}</span>
                {r.entity && <Badge c={G.textMuted} bg={G.input}>{r.entity}</Badge>}
              </div>
              <div style={{ fontSize:12, color:G.textMuted }}>
                {r.actor_email || 'system'} · {new Date(r.created_at).toLocaleString()}
              </div>
              {diff.length > 0 && <div style={{ marginTop:6, fontSize:12 }}>
                {diff.slice(0,3).map(d => (
                  <div key={d.key} style={{ padding:'2px 0' }}>
                    <span style={{ color:G.textMuted }}>{d.key}: </span>
                    <span style={{ color:G.red, textDecoration:'line-through' }}>{String(d.before ?? '—')}</span>
                    <span style={{ color:G.textMuted }}> → </span>
                    <span style={{ color:G.green }}>{String(d.after ?? '—')}</span>
                  </div>
                ))}
              </div>}
            </div>
          </div>;
        })}
      </div>}
  </div>;
}
